import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { VALUES } from "@/lib/site";
import { Reveal } from "@/lib/motion";

const PORTRAIT = "/images/hitanshu-portrait.webp";

const QUESTIONS = [
    "What is the idea?",
    "Who is the audience?",
    "What should they feel?",
    "How can cinematography carry it?",
];

export default function About() {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

    return (
        <section id="about" className="px-5 md:px-10 py-24 md:py-32">
            <div className="grid grid-cols-12 gap-y-8 md:gap-12">
                <div className="col-span-12 md:col-span-5">
                    <div className="md:sticky md:top-24">
                        <Reveal>
                            <div ref={imgRef} className="relative overflow-hidden aspect-[3/4]">
                                <motion.img
                                    data-testid="about-portrait"
                                    src={PORTRAIT}
                                    alt="Hitanshu Shah on set"
                                    style={{ y, scale: 1.14 }}
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            </div>
                            <div className="flex justify-between pt-3 text-[10px] uppercase tracking-[0.2em] text-inkdim">
                                <span>Hitanshu Bhadresh Shah</span>
                                <span>Mumbai</span>
                            </div>
                        </Reveal>
                    </div>
                </div>

                <div className="col-span-12 md:col-span-7">
                    <Reveal>
                        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-inkdim">Visual identity</p>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-6 font-display font-extrabold uppercase tracking-[-0.02em] leading-[0.95] text-4xl md:text-6xl">
                            Every frame should feel{" "}
                            <em className="font-serif italic font-medium normal-case">intentional.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 max-w-xl space-y-5 text-base md:text-lg leading-relaxed text-inkdim">
                            <p>
                                I watch light, texture, colour and the small details most people walk past — then
                                figure out how they become story.
                            </p>
                            <p>
                                I'd rather experiment than follow a formula. A luxury brand, a corporate film, a
                                music video and a documentary each deserve their own visual language — not the same
                                borrowed "cinematic look".
                            </p>
                            <p>
                                I stay hands-on from the first idea to the final output — writing, lighting,
                                shooting and cutting with the same curiosity.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-12">
                        {QUESTIONS.map((q, i) => (
                            <Reveal key={q} delay={i * 0.06}>
                                <p
                                    className="font-serif italic text-2xl md:text-4xl py-5 border-b border-line"
                                    data-testid={`about-question-${i}`}
                                >
                                    {q}
                                </p>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1}>
                        <div className="mt-10 flex flex-wrap gap-3">
                            {VALUES.map((v) => (
                                <span
                                    key={v}
                                    className="border border-ink/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-ink hover:text-paper transition-colors duration-300 cursor-default"
                                >
                                    {v}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
